const HttpClient = require('server/helpers/HttpClient');
const httpClient = HttpClient.getInstance();
const { getWeakTicket } = require('shared/clientUtils');
const { companyInfoSelectors } = require('store/companyInfo/selectors');
const { SplunkReporter } = require('reporting/splunk/SplunkReporter');
const { isServer } = require('../../shared/utils');
const splunkReporter = SplunkReporter.getInstance();
const logger = 'server/routes/rest/bfs/index';

class WavefrontReporter {
  init({
    portal,
    domainId,
    entityId,
    realmId,
    token,
    ticket,
    isUserSignedIn,
    syncToken,
    authToken,
    ssrtid,
    pageName,
    language,
    region,
    offeringId,
    appSourceOffering,
    type,
  }) {
    this.baseEndpoint = `/${portal}/rest/reporting/prometheus`;
    this.headers = getWeakTicket({
      domainId,
      entityId,
      realmId,
      token,
      ticket,
      isUserSignedIn,
      syncToken,
      authToken,
      ssrtid,
    });
    this.ssrtid = ssrtid;
    this.token = token;
    this.baseTags = {
      flow: pageName,
      locale: companyInfoSelectors.localeSelector({ region, language }),
      offeringId,
      appSourceOffering,
      saleType: type,
    };

    if (!isServer() && window && window.location && window.location.href) {
      this.baseTags.fullPageUrl = window.location.href;
    }
  }

  static getInstance() {
    if (!WavefrontReporter._instance) {
      WavefrontReporter._instance = new WavefrontReporter();
    }
    return WavefrontReporter._instance;
  }

  registerPageLoadObserver() {
    const perfObserver = new PerformanceObserver((observedEntries) => {
      observedEntries.getEntries().forEach((entry) => {
        if (entry.duration === 0) {
          return;
        }
        this.trackPageLoad({ timeInMs: entry.domContentLoadedEventEnd });
        perfObserver.disconnect();
      });
    });

    perfObserver.observe({
      type: 'navigation',
      buffered: true,
    });
  }

  async trackView() {
    await this.track({ type: 'view' });
  }

  async trackPayment({ paymentMethod, status, subFlow, isPaymentUsingWallet }) {
    await this.track({
      type: 'payment',
      data: { paymentMethod, status, subFlow, isPaymentUsingWallet },
    });
  }

  async trackPageLoad({ timeInMs }) {
    await this.track({ type: 'pageLoad', data: { timeInMs } });
  }

  async trackInterceptedNetworkRequests({ endpoint, responseCode, executionTimeMs, logType }) {
    await this.track({
      type: 'track_intercepted_network_requests',
      data: { endpoint, responseCode, executionTimeMs, logType },
    });
  }

  async trackFrictionlessLoginOpened(token) {
    await this.track({ type: 'frictionless/login-opened', data: { token } });
  }

  async trackFrictionlessSignupViewed(token) {
    await this.track({ type: 'frictionless/signup-viewed', data: { token } });
  }

  async trackFrictionlessSignupError(issue, token) {
    await this.track({
      type: 'frictionless/signup-error',
      data: { issue, token },
    });
  }

  async trackFrictionlessLoginError(issue, token) {
    await this.track({
      type: 'frictionless/login-error',
      data: { issue, token },
    });
  }

  async trackUsernameAvailabilityCheck({ isAvailable, executionTimeMs, status }) {
    await this.track({
      type: 'username/availability-check',
      data: { isAvailable, executionTimeMs, status },
    });
  }

  async trackSignInCTAViewed(token) {
    await this.track({
      type: 'frictionless/sign-in-cta-viewed',
      data: { token },
    });
  }

  async trackSignInCTAClicked(token) {
    await this.track({
      type: 'frictionless/sign-in-cta-clicked',
      data: { token },
    });
  }

  async trackFrictionlessPhase3IdentityWidgetCanceled(token) {
    await this.track({
      type: 'frictionless/phase3-identity-widget-canceled',
      data: { token },
    });
  }

  async trackFrictionlessPhase3IdentityWidgetCompleted(token) {
    await this.track({
      type: 'frictionless/phase3-identity-widget-completed',
      data: { token },
    });
  }

  async trackFrictionlessPhase3IdentityWidgetError(token) {
    await this.track({
      type: 'frictionless/phase3-identity-widget-error',
      data: { issue: 'identity', token },
    });
  }

  async trackGetMerchantOwnedWalletsSuccess() {
    await this.track({
      type: 'merchant-owned-wallets/get-success',
      data: {},
    });
  }

  async trackGetMerchantOwnedWalletsError() {
    await this.track({
      type: 'merchant-owned-wallets/get-error',
      data: {},
    });
  }

  async trackDeleteMerchantOwnedWalletSuccess() {
    await this.track({
      type: 'merchant-owned-wallets/delete-success',
      data: {},
    });
  }

  async trackDeleteMerchantOwnedWalletError() {
    await this.track({
      type: 'merchant-owned-wallets/delete-error',
      data: {},
    });
  }

  async trackCloneToMerchantWalletSuccess() {
    await this.track({
      type: 'merchant-owned-wallets/clone-success',
      data: {},
    });
  }

  async trackCloneToMerchantWalletError() {
    await this.track({
      type: 'merchant-owned-wallets/clone-error',
      data: {},
    });
  }

  async trackCreateMerchantOwnedDurableWalletSuccess() {
    await this.track({
      type: 'merchant-owned-wallets/create-success',
      data: {},
    });
  }

  async trackCreateMerchantOwnedDurableWalletError() {
    await this.track({
      type: 'merchant-owned-wallets/create-error',
      data: {},
    });
  }

  async trackSaveMerchantWalletCheckboxViewed() {
    await this.track({
      type: 'merchant-owned-wallets/save-checkbox-viewed',
      data: {},
    });
  }
  async trackCofSuccessScreenViewed({ savedMerchantWalletStatus, isScheduledPayment }) {
    await this.track({
      type: 'cof-success-screen-viewed',
      data: { savedMerchantWalletStatus, isScheduledPayment },
    });
  }

  async trackForcedAutopayInvoiceViewed() {
    await this.track({
      type: 'forced-autopay/invoice-viewed',
      data: {},
    });
  }

  async trackForcedAutopayButtonClicked() {
    await this.track({
      type: 'forced-autopay/pay-button-clicked',
      data: {},
    });
  }

  async trackForcedAutopayScheduledViewed({ isPostSchedule }) {
    await this.track({
      type: 'forced-autopay/scheduled-viewed',
      data: { isPostSchedule },
    });
  }

  async trackOrphanedInvoiceViewed() {
    await this.track({
      type: 'forced-autopay/orphaned-invoice-viewed',
      data: {},
    });
  }

  async trackOrphanedInvoicePayButtonClicked() {
    await this.track({
      type: 'forced-autopay/orphaned-invoice-pay-button-clicked',
      data: {},
    });
  }

  // COF Manage Screen tracking
  async trackCofManageScreenViewed() {
    await this.track({
      type: 'cof/manage-screen-viewed',
      data: {},
    });
  }

  async trackCofManageScreenError() {
    await this.track({
      type: 'cof/manage-screen-error',
      data: {},
    });
  }

  async trackCofDeleteWalletSuccess() {
    await this.track({
      type: 'cof/delete-wallet-success',
      data: {},
    });
  }

  async trackCofDeleteWalletError() {
    await this.track({
      type: 'cof/delete-wallet-error',
      data: {},
    });
  }

  async track({ type, data = {} }) {
    try {
      if (!this.baseEndpoint) {
        // wavefront not initiated
        throw new Error('Tried to track with wavefront not initiated');
      }

      const url = `${this.baseEndpoint}/${type}`;
      await httpClient({
        url,
        method: 'POST',
        data: { ...this.baseTags, ...data },
        headers: this.headers,
        endpoint: url,
      });
    } catch (e) {
      splunkReporter.contextual({
        sessionId: this.ssrtid,
        logInfo: { logLevel: 'error', logger },
        event: 'report',
        action: 'WF',
        activityInfo: {
          type,
          data,
        },
        error: {
          message: e.message,
          stack: e.stack,
        },
      });
    }
  }
}

module.exports = WavefrontReporter;
